import { useEffect, useMemo, useState } from "react";
import { Button, Card, CardHeader, CardContent, Box, Grid, Divider } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGrid, GridCallbackDetails, GridColDef, GridFilterModel } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { FormInput } from "../../components/FormInput";
import { FormAsyncAutocomplete } from "../../components/FormAsyncAutoComplete";
import { CustomTableToolbar } from "../../components/CustomTableToolbar";
import { ConfirmationDialog } from "../../components/ConfirmationDialog";
import { FormAutocomplete } from "../../components/FormAutoComplete";
import useNotifications from "../../components/NotificationProvider";
import LocationService from "../../services/location.service";
import OrganizationService from "../../services/organization.service";
import { getTimeZones } from "../../global/timezones";
import { IRowFilter, ITableRowsState } from "../../types/app/common.type";
import { ILocation } from "../../types/api/location.type";

const Locations = () => {
  const { t } = useTranslation("common");
  const {
    control,
    register,
    reset,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm({});
  const [openLocationDialog, setOpenLocationDialog] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [rowsState, setRowsState] = useState<ITableRowsState>({
    page: 0,
    pageSize: 5,
  });
  const [totalRows, setTotalRows] = useState<number>(0);
  const [rowFilter, setRowFilter] = useState<IRowFilter>({});
  const [tableRows, setTableRows] = useState<ILocation[]>([]);
  const [selectedRow, setSelectedRow] = useState<ILocation[]>([]);
  const [searchText, setSearchText] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [filterButtonEl, setFilterButtonEl] = useState<HTMLButtonElement | null>(null);
  const { setNotification } = useNotifications();

  const columns: GridColDef[] = [
    { field: "loc_id", headerName: "Location ID", flex: 1 },
    { field: "loc_name", headerName: "Name", flex: 1 },
    { field: "org_id", headerName: "Organization ID", flex: 1 },
    { field: "geo", headerName: "Geo", flex: 1 },
    { field: "timezone", headerName: "Timezone", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => {
        if (params.row.deleted) {
          return <></>;
        }
        return (
          <>
            <EditIcon
              sx={{ cursor: "pointer", marginRight: "10px" }}
              onClick={() => {
                console.log("edit---", params);
                setOpenLocationDialog(true);
                reset({ ...params.row });
              }}
            />
            <DeleteIcon
              sx={{ cursor: "pointer", marginRight: "10px" }}
              onClick={() => {
                console.log("delete---", params);
                setSelectedRow([{ ...params.row }]);
                setOpenDeleteConfirmation(true);
              }}
            />
          </>
        );
      },
    },
  ];

  useEffect(() => {
    getLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsState, rowFilter, showDeleted]);

  useEffect(() => {
    if (searchText) {
      const delayDebounceFn = setTimeout(() => {
        setRowFilter({ loc_name: searchText });
      }, 1000);
      return () => clearTimeout(delayDebounceFn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const timezones = useMemo(getTimeZones, []);

  const getLocations = async () => {
    try {
      const result = await LocationService.getLocations({
        count_per_page: rowsState.pageSize,
        page_no: rowsState.page + 1,
        filter: rowFilter,
        show_deleted: showDeleted,
      });
      setTotalRows(result.data?.total);
      setTableRows(result.data.results);
    } catch (e) {
      console.error("----", e);
    }
  };

  const onSubmit = async (values: any) => {
    try {
      let loc_uuid = values.loc_uuid;
      values.org_id = values?.org_id.org_id;
      values.geo = {};
      if (loc_uuid) {
        delete values.loc_id;
        await LocationService.editLocation(values, loc_uuid);
      } else {
        await LocationService.createLocation(values);
      }
      setOpenLocationDialog(false);
      setNotification({
        message: `Location ${loc_uuid ? "updated" : "created"}successfully`,
        open: true,
      });
      getLocations();
      reset({});
    } catch (e) {
      setNotification({
        message: "Error occurred",
        open: true,
        type: "error",
      });
      console.error("----", e);
    }
  };

  const handleDelete = async () => {
    try {
      await LocationService.deleteLocation(selectedRow[0].loc_uuid as string);
      setOpenDeleteConfirmation(false);
      setNotification({
        message: "Location deleted successfully",
        open: true,
      });
      getLocations();
    } catch (e) {
      setNotification({
        message: "Error occurred",
        open: true,
        type: "error",
      });
      console.error("----", e);
    }
  };

  const onFilterChange = (model: GridFilterModel, details: GridCallbackDetails) => {
    console.log("filter data---", model, details);
    setRowFilter({ [model.items[0].columnField]: model.items[0].value });
  };

  const handleSearchChange = (searchText: string) => {
    setSearchText(searchText);
  };

  const handleClearSearch = () => {
    setSearchText("");
    setRowFilter({});
  };

  return (
    <>
      <Card sx={{ backgroundColor: "#2A2B31" }}>
        <CardHeader
          action={
            <Button
              sx={{ marginRight: "10px" }}
              variant="contained"
              onClick={() => {
                setOpenLocationDialog(true);
              }}
            >
              Create
            </Button>
          }
          title={t("common:locations.title")}
        />
        <CardContent
          sx={{
            paddingBottom: "16px !important",
            "& .deleted-row": {
              backgroundColor: "#4c3939",
            },
          }}
        >
          <DataGrid
            autoHeight
            rows={tableRows}
            columns={columns}
            getRowId={(row) => row.loc_id}
            rowsPerPageOptions={[rowsState.pageSize]}
            rowCount={totalRows}
            disableSelectionOnClick
            getRowClassName={(params) => (params.row.deleted ? "deleted-row" : "")}
            paginationMode="server"
            // sortingMode="server"
            // onSortModelChange={handleSortModelChange}
            filterMode="server"
            onFilterModelChange={onFilterChange}
            {...rowsState}
            onPageChange={(page) => setRowsState((prev: ITableRowsState) => ({ ...prev, page }))}
            components={{
              Toolbar: CustomTableToolbar,
            }}
            componentsProps={{
              panel: {
                anchorEl: filterButtonEl,
              },
              toolbar: {
                setFilterButtonEl,
                searchValue: searchText,
                onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                  handleSearchChange(event.target.value);
                },
                showDeleted: showDeleted,
                onShowDeletedChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                  console.log(event.target.checked);
                  setShowDeleted(event.target.checked);
                },
                clearSearch: () => handleClearSearch(),
              },
            }}
          />
        </CardContent>
      </Card>
      <Dialog fullWidth={true} maxWidth="md" open={openLocationDialog} onClose={() => setOpenLocationDialog(false)}>
        <DialogTitle>{`${getValues("loc_id") ? "Edit" : "Create"} location`}</DialogTitle>
        <Divider />
        <DialogContent>
          <Box component="main">
            <form>
              <Grid container spacing={2} mt={1} pr={2} pb={2} sx={{ overflow: "auto" }}>
                <Grid item xs={6}>
                  <FormInput
                    name="loc_name"
                    type="string"
                    id="loc_name"
                    label="Name"
                    rules={{
                      required: "This field is required",
                    }}
                    control={control}
                    register={register}
                    errors={errors}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormAsyncAutocomplete
                    name="org_id"
                    id="org_id"
                    label="Organization"
                    getOptions={(searchTerm: string) =>
                      OrganizationService.getOrganizations({
                        count_per_page: 10,
                        page_no: 1,
                        filter: { org_name: searchTerm },
                        show_deleted: false,
                      })
                    }
                    labelKey="org_name"
                    valueKey="org_id"
                    rules={{
                      required: "This field is required",
                    }}
                    control={control}
                    register={register}
                    errors={errors}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormInput
                    name="geo"
                    type="string"
                    id="geo"
                    label="Geo location"
                    control={control}
                    register={register}
                    errors={errors}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormAutocomplete
                    name="timezone"
                    type="string"
                    id="timezone"
                    label="Location timezone"
                    options={timezones}
                    labelKey="label"
                    valueKey="label"
                    rules={{
                      required: "This field is required",
                    }}
                    control={control}
                    register={register}
                    errors={errors}
                  />
                </Grid>
              </Grid>
            </form>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button sx={{ mr: 2 }} onClick={() => setOpenLocationDialog(false)}>
            Cancel
          </Button>
          <Button sx={{ mr: 2 }} variant="contained" onClick={handleSubmit(onSubmit)}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        open={openDeleteConfirmation}
        setOpen={setOpenDeleteConfirmation}
        onProceed={handleDelete}
        title="Delete"
        content="If you are sure you want to delete the selected location?"
        confirmationText={selectedRow[0]?.loc_name}
      />
    </>
  );
};

export default Locations;
